const COMPLAINTS = [
  [/numb/, 'Numbness'],
  [/neck/, 'Pain, neck'],
  [/arm|shoulder/, 'Pain, arm'],
  [/back/, 'Pain, back'],
  [/leg|radicul|sciatic/, 'Pain, leg'],
  [/walk|claudication/, 'Walking difficulties'],
  [/weak/, 'Weakness'],
];

function text(value) {
  if (value == null) return '';
  return String(value).trim();
}

function oneDecimal(value) {
  const number = Number(value);
  if (!Number.isFinite(number) || number <= 0) return '';
  return String(Math.round(number * 10) / 10);
}

function ageLabel(value) {
  const number = Number(value);
  if (!Number.isFinite(number) || number < 0 || number > 130) return '';
  return String(Math.round(number));
}

function sexLabel(value) {
  const token = text(value).toUpperCase();
  if (token === 'F' || token === 'FEMALE') return 'Female';
  if (token === 'M' || token === 'MALE') return 'Male';
  if (token === 'U' || token === 'UNDIFFERENTIATED') return 'Undifferentiated';
  if (token === 'UNKNOWN') return 'Unknown';
  return '';
}

function complaintLabel(value) {
  const token = text(value).toLowerCase();
  if (!token) return '';
  const found = COMPLAINTS.find(([pattern]) => pattern.test(token));
  return found ? found[1] : '';
}

function hasPriorSpineSurgery(value, revision) {
  if (text(revision) === '1') return true;
  if (Array.isArray(value)) return value.length > 0;
  const token = text(value).toLowerCase();
  if (!token || token === '0' || token === 'no' || token === 'none') return false;
  return true;
}

function assign(target, key, value) {
  if (value == null || value === '') return;
  target[key] = value;
}

function formPatch(forms, formId, values) {
  const filled = {};
  Object.entries(values).forEach(([key, value]) => assign(filled, key, value));
  if (!Object.keys(filled).length) return;
  const id = `${formId}::case`;
  const prior = forms[id] || { values: {}, repeats: {} };
  forms[id] = {
    ...prior,
    values: { ...(prior.values || {}), ...filled },
  };
}

function addHistoryTerm(forms, term) {
  const label = text(term);
  if (!label) return;
  const id = 'history::case';
  const prior = forms[id] || { values: {}, repeats: {} };
  const values = { ...(prior.values || {}) };
  const slots = Object.entries(values).filter(([key]) => (
    key === 'alliance.history.term' || /^alliance\.history\.term#\d+$/.test(key)
  ));
  if (slots.some(([, value]) => value === label)) return;
  let index = 0;
  while (values[`alliance.history.term#${index}`]) index += 1;
  values[`alliance.history.term#${index}`] = label;
  const repeats = { ...(prior.repeats || {}) };
  if (index > 0) repeats.medical_history = Math.max(repeats.medical_history || 1, index + 1);
  forms[id] = { ...prior, values, repeats };
}

function allianceStudy(handoff) {
  return (handoff?.studies || []).find((study) => study.id === 'alliance') || null;
}

/** Copy the Joint Decisions characteristics onto the Alliance chart. Joint Decisions wins on fields it knows. */
export function applyJointDecisionsHandoff(state, handoff, { focusConsent = false } = {}) {
  const base = state || { active: null, forms: {} };
  if (!handoff) return base;
  const characteristics = handoff.characteristics || {};
  const forms = { ...(base.forms || {}) };
  const baseline = {};
  const indication = {};
  const spine = {};
  const operative = {};

  const age = ageLabel(characteristics.age);
  if (age) {
    assign(baseline, 'alliance.baseline.age', age);
    assign(baseline, 'alliance.baseline.age_90_or_older', Number(age) >= 90 ? 'Yes' : 'No');
  }
  assign(
    baseline,
    'alliance.baseline.sex_at_birth',
    sexLabel(characteristics.sex) || sexLabel(characteristics.sex_code),
  );
  const height = oneDecimal(characteristics.height_inches);
  if (height) {
    assign(baseline, 'alliance.baseline.height', height);
    assign(baseline, 'alliance.baseline.height_unit', 'in');
  }
  const weight = oneDecimal(characteristics.weight_lbs);
  if (weight) {
    assign(baseline, 'alliance.baseline.weight', weight);
    assign(baseline, 'alliance.baseline.weight_unit', 'lb');
  }
  const bmi = oneDecimal(characteristics.bmi)
    || (height && weight
      ? oneDecimal((Number(weight) / (Number(height) ** 2)) * 703)
      : '');
  assign(baseline, 'alliance.baseline.bmi', bmi);

  const smoking = text(characteristics.smoking).toUpperCase();
  if (smoking === 'YES') assign(baseline, 'alliance.baseline.nicotine.status', 'Current - Daily');
  else if (smoking === 'NO') assign(baseline, 'alliance.baseline.nicotine.status', 'Never');

  const diabetes = text(characteristics.diabetes);
  if (diabetes === '2') assign(baseline, 'alliance.baseline.diabetes_meds', 'Yes');
  else if (diabetes === '0' || diabetes === '1') assign(baseline, 'alliance.baseline.diabetes_meds', 'No');

  const insurance = text(characteristics.insurance);
  if (insurance === 'workers_comp') {
    assign(indication, 'alliance.indication.workers_comp', 'Yes');
    assign(baseline, 'alliance.baseline.payer', 'Other');
    assign(baseline, 'alliance.baseline.payer_other', "Workers' compensation");
  } else if (insurance === 'other') {
    assign(indication, 'alliance.indication.workers_comp', 'No');
  }

  assign(indication, 'alliance.indication.primary_complaint', complaintLabel(characteristics.chief_complaint));
  if (hasPriorSpineSurgery(characteristics.previous_spine_surgeries, characteristics.revision_surgery)) {
    assign(spine, 'alliance.spine_history.any', 'Yes');
  }

  const chart = allianceStudy(handoff)?.chart || {};
  assign(indication, 'alliance.indication.case_type', chart.caseType);
  assign(operative, 'alliance.index.date', text(handoff.surgeryDate).slice(0, 10));
  assign(operative, 'alliance.index.procedure#0', chart.procedure);

  formPatch(forms, 'baseline', baseline);
  formPatch(forms, 'indication', indication);
  formPatch(forms, 'spine-history', spine);
  formPatch(forms, 'operative', operative);
  if (diabetes === '2') addHistoryTerm(forms, 'Diabetes');
  else if (diabetes === '1') addHistoryTerm(forms, 'Prediabetes');

  const consent = allianceStudy(handoff)?.consent;
  const active = focusConsent && consent
    ? { phaseId: consent.phaseId, visitId: consent.visitId || '', formId: consent.formId }
    : base.active;

  return { ...base, forms, active };
}
